import React, { useState, useContext, useEffect } from 'react'
import Link from 'next/link'
import { FaInfoCircle } from 'react-icons/fa'
import { MdNotificationsActive } from 'react-icons/md'
import { IoHelpBuoyOutline } from 'react-icons/io5'
import { useSetCookie, useDeleteCookie } from 'cookies-next'
import { useRouter } from 'next/router'
import { Button, Input, Tooltip, Checkbox, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from "@heroui/react"

import Layout from '../components/layout'
import ContentArea from '../components/content-area'
import Header from '../components/header'
import Footer from '../components/footer'
import Divider from '../components/divider'
import Loading from '../components/loading'
import Upload from '../components/upload'
import StatusChip from '../components/status-chip'
import AccountOptions from '../components/account-options'
import ErrorChip from '../components/error-chip'

import { inputStyle } from '../src/styles'
import { UserContext } from '../contexts/user-context'
import useHasMounted from '../hooks/use-has-mounted'
import { getFlags } from '../src/get-flags'

const Account = ({ adminRef, ember }) => {
  const hasMounted = useHasMounted()
  const userHook = useContext(UserContext)
  userHook.requireSignin(userHook.loggedIn)
  const user = userHook.user
  const router = useRouter()
  
  const [loading, setLoading] = useState(false)
  const [success, setSucess] = useState(false)
  const [postError, setPostError] = useState('')
  const [errors, setErrors] = useState({})
  const [data, setData] = useState({...user})
  const [pushStatus, setPushStatus] = useState(null)
  const [isNativeApp, setisNativeApp] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const modal = useDisclosure()

  const setCookie = useSetCookie()
  const deleteCookie = useDeleteCookie()
  
  useEffect(() => {
    if (window.isNativeApp) {
      setisNativeApp(true)
      window.setPushStatus = (val) => {
        setPushStatus(val)
      }
    }
  }, [])

  const handleChange = (attr, value) => {
    const toUpdate = Object.assign({}, data)
    toUpdate[attr] = value
    setSucess(false)
    setPostError('')
    setErrors({})
    setData(toUpdate)
  }

  const validate = () => {
    const validation = {}
    if (!data.email) validation.email = 'Required'
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    if (!re.test(String(data.email).toLowerCase())) {
      validation.email = 'Invalid Email'
    }
    if (Object.keys(validation).length) {
      setErrors(validation)
      return false
    }
    return true
  }

  const submit = async () => {
    if (!validate()) return
    setLoading(true)
    try {
      const { ['challengesJoined']: remove1, ['groupsJoined']: remove2, ['organizations']: remove3, ['integrations']: remove4, ...trimmed } = data
      if (trimmed.photo) trimmed.photo = trimmed.photo.replace('-originals', '')
      const putConfig = {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        },
        body: JSON.stringify(trimmed)
      }
      const res = await fetch(`${process.env.API}/users/${user.id}`, putConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setError(errMsg)
      }
      userHook.mutate()
      
      if (window.ReactNativeWebView) {
        // send request to update native app session
        window.ReactNativeWebView?.postMessage(JSON.stringify({ refresh: true }))
      }

      window.scrollTo(0, 0)
    
      setSucess(true)
      setPostError('')
      setLoading(false)
      setErrors({})
    } catch (err) {
      console.log(err)
      setPostError('Problem saving')
      setLoading(false)
    }
  }

  const deleteAccount = async () => {
    setIsDeleting(true)
    try {
      const deleteConfig = {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': user.token
        }
      }
      const res = await fetch(`${process.env.API}/users/${user.id}`, deleteConfig)
      if (!res.ok) {
        const errMsg = await res.text()
        setError(errMsg)
      }
    
      if (window.Beacon) {
        Beacon('identify', {})
      }
      window.ReactNativeWebView?.postMessage(JSON.stringify({ logout: true }))
      userHook.requireSignin()
    } catch (err) {
      console.log(err)
      setPostError('Problem deleting account')
      setIsDeleting(false)
      close()
    }
  }

  const toggleFlag = () => {
    const cookieName = '__vercel_flags'
    if (ember) {
      deleteCookie(cookieName)
    } else {
      setCookie(cookieName, JSON.stringify({ ember: true }), {
        path: '/',
        maxAge: 60 * 60 * 24 * 7 // 1 week
      })
    }
    router.reload()
  }

  return (
    <Layout
      title='Challenge Hound: Account'
      description='Update your account settings'
      keywords='challenge hound account'>
      <Header section='account' main='true' />
      <main>
        <ContentArea>
          <Divider>Your Account</Divider>
          
          {isNativeApp && <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '35px' }}>
            <Button 
              onPress={() => { window.ReactNativeWebView?.postMessage(JSON.stringify({ support: true })) }}
              startContent={<IoHelpBuoyOutline className='text-primary mr-2' style={{ fontSize: '25px', verticalAlign: 'middle'  }}/>}
              >
              Challenge Hound Help &amp; Support
            </Button>
          </div>}
          
          {hasMounted && <AccountOptions page='account' />}

          {hasMounted && <div style={{ maxWidth: '500px', margin: '0px auto 40px auto' }}>
            
            {success && <StatusChip color='success' onClose={() => { setSucess(false) }} msg='Your changes have been saved!' />}
            {postError && <StatusChip color='danger' onClose={() => { setPostError('') }} msg={<>Error processing your request. Please ensure the email you are entering is unique. If you already created an account with this email, you&apos;ll have to sign in with that account.&nbsp;<Link href='/contact' as='/contact'>Contact us</Link> if you&apos;re stuck.</>} />}

            {userHook.isAnonymous(user) && <div className='border-2 border-red-500 rounded-md p-3 mb-6'>
              <h3 className='text-lg font-bold mb-2 text-danger'>Please Update Your Name &amp; Email</h3>
              It looks like you have signed up using Apple&quot;s anonymous feature. Challenge Hound works best when participants are <strong>NOT Anonymous</strong>. More in our <Link className='text-info' href='/privacy'>privacy policy</Link>.
              <p className='mt-2'>If you <strong>already</strong> signed up with Challenge Hound, then you probably just created a duplicate account by accident. Please copy the anonymous email address below and send a message to Challenge Hound support to have the accounts merged.</p>
            </div>}

            {(adminRef?.token && adminRef?.id && adminRef?.id !== user.id) && <div className='mb-12'>
              <div>
                <Button
                  color='primary'
                  onPress={() => { 
                    router.push(`/account-v2/houndmin/impersonate`)
                   }}
                  >Back to Admin Account</Button>
              </div>
            </div>}

            <div className='mb-12'>
              <Upload
                label={<>Profile Photo <span className='ml-2 text-sm text-gray-500'>(square dimension works best)</span></>}
                type='profile'
                token={user.token}
                existingUrl={data.photo}
                onChange={(data) => {
                  if (!data) {
                    handleChange('photo', null)
                    return
                  }
                  const loader = new Image()
                  loader.src = `${process.env.CDN}/${data.key}`
                  handleChange('photo', `${process.env.CDN}/${data.key}`)
                }}
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 md:gap-2'>
              <div className='mb-12'>
                <div className='text-xl flex items-center mb-2 '>
                  First Name
                  <ErrorChip errors={errors} setErrors={setErrors} errorKey='fname' />
                </div>
                <Input
                  name='fname'
                  maxLength={50}
                  placeholder=''
                  value={data.fname}
                  size='lg'
                  radius='sm'
                  classNames={ inputStyle }
                  onChange={(e) => {
                    handleChange('fname', e.target.value)
                  }} />
              </div>
              <div className='mb-12'>
                <div className='text-xl flex items-center mb-2 '>
                  Last Name
                  <ErrorChip errors={errors} setErrors={setErrors} errorKey='lname' />
                </div>
                <Input
                  name='lname'
                  maxLength={50}
                  placeholder=''
                  value={data.lname}
                  size='lg'
                  radius='sm'
                  classNames={ inputStyle }
                  onChange={(e) => {
                    handleChange('lname', e.target.value)
                  }} />
              </div>
            </div>

            <div className='mb-12'>
              <div className='text-xl flex items-center mb-2 '>
                Email Address&nbsp;<Tooltip classNames={{ base: 'max-w-60' }} color='primary'
                  content="Your email is used to sign in and to receive challenge status updates. Challenge Hound does not disclose your email to any third parties, including challenge organizers."
                ><Button variant='light' size='sm'><FaInfoCircle style={{ fontSize: '18px', color: user.email ? 'black' : 'red' }}/></Button></Tooltip>
                <ErrorChip errors={errors} setErrors={setErrors} errorKey='email' />
              </div>
              <Input
                name='email'
                maxLength={50}
                placeholder=''
                value={data.email}
                size='lg'
                radius='sm'
                classNames={ inputStyle }
                onChange={(e) => {
                  handleChange('email', e.target.value)
                }} />
            </div>

            {user.organizationId && <div className='mb-12'>
              <div className='text-xl flex items-center mb-2 '>Organization</div>
              <div><Link href='/organization' as={`/organization`}>{user.organization.name}</Link></div>
            </div>}
            
            <div className='mb-12'>
              <div className='text-xl flex items-center mb-2'>
                Select Units and Measurements
                <ErrorChip errors={errors} setErrors={setErrors} errorKey='imperial' />
              </div>
              <Checkbox classNames={{ wrapper: 'bg-white' }} color='secondary'
                isSelected={data.imperial}
                onChange={() => {
                  handleChange('imperial', true)
                }} 
                >Miles &amp; Feet</Checkbox>
              &nbsp; &nbsp;
              <Checkbox classNames={{ wrapper: 'bg-white' }} color='secondary'
                isSelected={!data.imperial}
                onChange={() => {
                  handleChange('imperial', false)
                }} 
                >Meters &amp; Kilometers</Checkbox>
            </div>

            <div className='mb-12'>
              <div className='text-xl flex items-center mb-2'>
                Enable Cookies
                <ErrorChip errors={errors} setErrors={setErrors} errorKey='cookies' />
              </div>
              <Checkbox classNames={{ wrapper: 'bg-white' }} color='secondary'
                isSelected={data.cookies === true}
                onChange={() => {
                  handleChange('cookies', true)
                }} 
                >Allow Cookies</Checkbox>
              &nbsp; &nbsp;
              <Checkbox classNames={{ wrapper: 'bg-white' }} color='secondary'
                isSelected={data.cookies === false}
                onChange={() => {
                  handleChange('cookies', false)
                }} 
                >No More Cookies</Checkbox>
            </div>

            <div className='mb-12'>
              <div className='text-chTan-600'>
                <Button
                  className='mr-2'
                  color='primary'
                  onPress={() => { toggleFlag() }}
                  >{ember ? 'Disable' : 'Enable'} Ember</Button>
                The newly designed version of Challenge Hound.
              </div>
            </div>

            {isNativeApp && <div className='mb-12'>
              <div className='text-xl flex items-center mb-2'>
                Push Notifications
              </div>
              <div>
                {pushStatus === null && <span style={{ textStyle: 'italic'}}>Loading...</span>}
                {pushStatus === 'granted' && <span style={{ color: 'green' }}>ENABLED</span>}
                {pushStatus === 'denied' && <>
                  <span style={{ color: 'red' }}>DISABLED</span>&nbsp;To enable, go to your phone settings and enable push notifications for the Challenge Hound app.
                </>}
                {pushStatus === 'undetermined' && <>
                  <Button 
                    onPress={() => { window.ReactNativeWebView?.postMessage(JSON.stringify({ requestPush: true })) }}
                    startContent={<MdNotificationsActive style={{ fontSize: '20px', verticalAlign: 'middle', marginRight: '5px'  }}/>}
                    >
                    Enable Push Notifications
                  </Button>
                </>}
              </div>
              <div className='mt-6 text-gray-400'>When enabled, Challenge Hound will push challenge and feed notices to your device instead of using email.</div>
            </div>}

            {isNativeApp && <div className='mb-12'>
              <div className='text-xl flex items-center mb-2'>
                App Version
              </div>
              <div>
                {window?.platformOs} {window?.nativeVersion} &nbsp;
                <a href='' onClick={() => { window.ReactNativeWebView?.postMessage(JSON.stringify({ logs: true })) }}>
                  View App Log
                </a>
              </div>
            </div>}

            <Button 
              color='primary' 
              disabled={Boolean(Object.keys(errors).length) || loading}
              onPress={() => { submit() }}
            >Save Profile</Button>
            
            <div className='text-gray-400 mt-24'>
              If you would like to completely delete your account <div className='inline cursor-pointer font-bold' onClick={() => { modal.onOpen() }}>click here</div>.
            </div>

            <Modal isOpen={modal.isOpen} onOpenChange={modal.onOpenChange}>
              <ModalContent>
                <ModalHeader className="flex flex-col gap-1">Delete Account?</ModalHeader>
                <ModalBody>
                  {isDeleting && <div className='text-center'>
                    <Loading />
                    Deleting Account...
                  </div>}

                  {!isDeleting && <div className='text-left mb-10'>
                    Your account and ALL related data will be permanently deleted and removed from Challenge Hound. This action cannot be undone.
                  </div>}
                </ModalBody>
                <ModalFooter>
                  <Button color="success" variant="light" onPress={modal.onClose}>
                    Cancel
                  </Button>
                  <Button color="danger" onPress={() => { deleteAccount() }}>Yes, Delete Account</Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </div>}
          
        </ContentArea>
      </main>
      <Footer />
    </Layout>
  );
}

export const getServerSideProps = (async ({ req }) => {
  const flags = getFlags(req)
  
  // Build absolute URL for your internal API
  const protocol = req.headers['x-forwarded-proto'] || 'http';
  const host = req.headers['x-forwarded-host'] || req.headers.host || 'localhost:3001';
  const baseUrl = `${protocol}://${host}`;

  const response = await fetch(`${baseUrl}/api/admin-session`, {
    method: 'GET',
    headers: {
      cookie: req.headers.cookie || '', // Forward the cookies
    },
  });

  const adminRef = await response.json();

  return { props: { adminRef, ember: Boolean(flags?.ember) } };
})

export default Account
